import { DataRole } from '@mode-switch/express';
import { QueryResult } from '@mode-switch/query';
import { ChartTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT } from '../charts/chart-helpers';
import {
  DECIMAL_MAX,
  getDefaultSettingsByColumn,
  getFormatFromShortcut,
  getPlainTextFormat,
  getTextFormatOptionsByShortcutType,
} from './table-format-helpers';

/**
 * Helper functions to expedite the state related format processing. These are more generic
 * and may be used across groupings. Encoding related functionality should go in the
 * table-state-helpers.ts file in the charts grouping.
 */

/**
 * Update alignment of a column and return new text format.
 */
export function updateAlignment(
  alignment: ChartTypes.HorizontalAlignment,
  activeColumn: string,
  textFormat: ChartTypes.TextFormat
): ChartTypes.TextFormat {
  const newTextFormat = {
    ...textFormat,
    horizontalAlignment: alignment,
  };

  return newTextFormat;
}

/**
 * Update text wrapping status of a column and return new text format.
 */
export function updateTextWrappingEnabled(textFormat: ChartTypes.TextFormat): ChartTypes.TextFormat {
  const isTextWrappingEnabled = !textFormat.textWrappingEnabled;

  const newTextFormat = {
    ...textFormat,
    textWrappingEnabled: isTextWrappingEnabled,
  };

  return newTextFormat;
}

/**
 * Update format of a column and return new text format.
 */
export function resetToShortcut(
  shortcutType: ChartTypes.FormatShortcutType,
  activeColumn: string,
  textFormat: ChartTypes.TextFormat
): ChartTypes.TextFormat {
  let newTextFormat: ChartTypes.TextFormat = {
    type: textFormat.type,
    colType: textFormat.colType,
    horizontalAlignment: textFormat.horizontalAlignment,
  };
  // If resetting to PlainText we need to user a different helper to get a specific
  // formatting based on column type. This shortcut is a special case, since formatter can
  // differ by colType.
  if (shortcutType === ChartTypes.FormatShortcutType.PlainText && textFormat.colType) {
    const defaultColumnFormat = getPlainTextFormat(activeColumn, textFormat.colType);
    newTextFormat.type = defaultColumnFormat.type;
    newTextFormat = {
      ...newTextFormat,
      ...defaultColumnFormat,
    };
    newTextFormat.shortcutType = shortcutType;
  } else {
    // set formatter for this shortcut (ie - some column types can take multiple formatters)
    newTextFormat.type = getFormatFromShortcut(shortcutType);
    newTextFormat = {
      ...newTextFormat,
      ...getTextFormatOptionsByShortcutType(shortcutType),
    };
    newTextFormat.shortcutType = shortcutType;
  }

  return newTextFormat;
}

/**
 * Helper to update the columns settings in response to encoding changes.
 *
 * This will remove no longer existing table columns from `format.flatTable.columns`,
 * and add initial default formats for new columns.
 */
export function generateColumnSettingsFromUpdatedEncoding(
  currentColumns: QueryResult.Column<DataRole>[],
  previousTableFormat: ChartTypes.ChartFormat['flatTable']
): ChartTypes.FlatTableColumnSettings[] {
  // Remove any column formats that are no longer present in the new encoding
  const previousColumnFormats = previousTableFormat.columns;

  let remainingColumnFormats = previousColumnFormats.filter(
    (colFormat) => currentColumns.findIndex((col) => col.source === colFormat.id) !== -1
  );

  // Confirm all remaining columns are still the same type, if not, reset colType, colFormatType, and formatOptions to
  // what the default would be for that column type. This wouldn't be common, but it's possible that a column could
  // retain the same name but have a different type.
  let validatedRemaininColumnFormats: ChartTypes.FlatTableColumnSettings[] = [];
  remainingColumnFormats.map((colFormat) => {
    const newColumn = currentColumns.find((col) => col.source === colFormat.id);

    if (!newColumn) {
      return;
    }

    if (colFormat.colType === newColumn.type.name) {
      validatedRemaininColumnFormats.push(colFormat);
    } else {
      const defaultFormat = getDefaultSettingsByColumn(newColumn);
      validatedRemaininColumnFormats.push({
        ...colFormat,
        colType: defaultFormat.colType,
      });
    }
  });

  // Get the net new columns now present in the encoding
  const newColumns = currentColumns.filter(
    (col) => validatedRemaininColumnFormats.findIndex((format) => format.id === col.source) === -1
  );

  // Generate new default formats for new columns
  const newColumnFormats = newColumns.map((col) => getDefaultSettingsByColumn(col));
  const columnFormats = validatedRemaininColumnFormats.concat(newColumnFormats);

  return columnFormats;
}

/**
 * Update precision of a column and return new viz format.
 */
export function updatePrecision(precisionChange: number, textFormat: ChartTypes.TextFormat): ChartTypes.TextFormat {
  let decimalPlaces = textFormat.precision ?? 1; // If for some reason precision is null here, provide a value that will result successfully and fill the state.
  const newDecimalPlaces = decimalPlaces + precisionChange;

  // These errors really shouldn't ever fire, but might be helpful to keep while we continue
  // to build out the rest of flat tables functionality to help debug. Making sure we don't
  // pass a negative number to toFixed.
  if (newDecimalPlaces < 0) {
    console.error('Decimal places cannot go below 0');
  } else if (newDecimalPlaces > DECIMAL_MAX) {
    console.error(`Decimal places cannot go above ${DECIMAL_MAX}`);
  } else {
    decimalPlaces = newDecimalPlaces;
  }

  textFormat = {
    ...textFormat,
    precision: decimalPlaces,
  };

  return textFormat;
}

/**
 * Update width of a column and return new viz format.
 */
export function updateColumnWidth(newWidth: number, activeColumn: string, format: ChartTypes.ChartFormat) {
  const formatColumns = format.flatTable.columns,
    formatColumn = formatColumns.find((col) => col.id === activeColumn);

  // If the target column is not found, return current format
  if (!formatColumn) {
    return format;
  }

  const newFormatColumns = formatColumns.map((col) => {
    if (col.id === formatColumn.id) {
      return {
        ...formatColumn,
        width: newWidth,
      };
    } else {
      return col;
    }
  });

  return updateFormatWithFlatTableColumns(newFormatColumns, format);
}

/**
 * Update table sizing style type for a flat type
 */
export function updateTableSizingStyle(styleType: ChartTypes.FlatTableSizingStyle, format: ChartTypes.ChartFormat) {
  const newGeneralFormat = {
    ...format.flatTable.general,
    sizingStyle: styleType,
  };
  return updateFormatWithGeneralOptions(newGeneralFormat, format);
}

/**
 * Helper to generate a format object to use in cases where format is being
 * explicitly set.
 */
export function generateFormatFromFlatTableFormat(
  flatTableFormat: ChartTypes.ChartFormat['flatTable']
): ChartTypes.ChartFormat {
  return updateFormatWithFlatTable(flatTableFormat, DEFAULT_CHART_FORMAT);
}

/**
 * Return a new viz format given a flatTable columns format.
 */
export function updateFormatWithFlatTableColumns(
  columns: ChartTypes.FlatTableColumnSettings[],
  format: ChartTypes.ChartFormat
): ChartTypes.ChartFormat {
  return {
    ...format,
    flatTable: {
      ...format.flatTable,
      columns: columns,
    },
  };
}

/**
 * Return a new viz format given a flatTable general format.
 */
export function updateFormatWithGeneralOptions(
  general: ChartTypes.FlatTableFormatting,
  format: ChartTypes.ChartFormat
): ChartTypes.ChartFormat {
  return {
    ...format,
    flatTable: {
      ...format.flatTable,
      general: general,
    },
  };
}

/**
 * Helper to set the create a new viz format object setting flatTable.
 */
export function updateFormatWithFlatTable(
  flatTableFormat: ChartTypes.ChartFormat['flatTable'],
  format: ChartTypes.ChartFormat
): ChartTypes.ChartFormat {
  return {
    ...format,
    flatTable: flatTableFormat,
  };
}

/**
 * Helper to update pageSize in format for flat table.
 */
export function updatePageSizeInFormat(format: ChartTypes.ChartFormat, pageSize: number): ChartTypes.ChartFormat {
  return {
    ...format,
    flatTable: {
      ...format.flatTable,
      general: {
        ...format.flatTable.general,
        pageSize,
      },
    },
  };
}
